import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, ChevronDown, ChevronUp, Bot, Sparkles, Mail, User, Calendar, MapPin, DollarSign, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  type: 'user' | 'bot';
  text: string;
  timestamp: Date;
}

interface QuickReply {
  text: string;
  action: () => void;
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travelDate: '',
    budget: '',
    interests: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const quickReplies: QuickReply[] = [
    { text: "Gorilla Trekking", action: () => handleQuickReply("Tell me about gorilla trekking") },
    { text: "Safaris", action: () => handleQuickReply("Safari options and prices") },
    { text: "City Tours", action: () => handleQuickReply("Available city tours") },
    { text: "Self Drive", action: () => handleQuickReply("Self drive safari info") }
  ];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        addBotMessage("👋 Hi! I'm your TravelCord Assistant. How can I help plan your Ugandan adventure?");
      }, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (text: string, type: 'user' | 'bot') => {
    setMessages(prev => [...prev, {
      id: Math.random().toString(36).substr(2, 9),
      type,
      text,
      timestamp: new Date()
    }]);
  };

  const addBotMessage = (text: string) => {
    setIsTyping(true);
    setTimeout(() => {
      addMessage(text, 'bot');
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickReply = (text: string) => {
    addMessage(text, 'user');
    handleBotResponse(text);
  };

  const handleBotResponse = (userMessage: string) => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('gorilla')) {
      addBotMessage("Gorilla trekking is our most popular experience! Permits start from $700 per person. Would you like to know more about available dates and packages?");
      suggestContactForm();
    } else if (lowerMessage.includes('safari')) {
      addBotMessage("We offer various safari packages across Uganda's national parks. Our most popular options include:\n\n• 3-day Murchison Falls Safari ($450)\n• 4-day Queen Elizabeth Safari ($600)\n• 7-day Pearl of Africa Safari ($1,200)\n\nWould you like more details about any of these?");
      suggestContactForm();
    } else if (lowerMessage.includes('city') || lowerMessage.includes('tour')) {
      addBotMessage("Our city tours include:\n\n• Kampala Walking Tour ($32)\n• Complete Kampala Experience ($25)\n• Food & Culture Tour ($77)\n• Jinja Adventure Tours (from $25)\n\nWhich one interests you?");
      suggestContactForm();
    } else if (lowerMessage.includes('self drive')) {
      addBotMessage("Our self-drive options include 4x4 vehicles fully equipped for safari. Prices start from $80/day. Would you like to see our vehicle options and availability?");
      suggestContactForm();
    } else if (lowerMessage.includes('price') || lowerMessage.includes('cost')) {
      addBotMessage("Our experiences range from $25 for city tours to $700+ for premium experiences like gorilla trekking. What's your budget range? I can help find the perfect options for you.");
      suggestContactForm();
    } else if (lowerMessage.includes('book') || lowerMessage.includes('reserve')) {
      addBotMessage("I'll help you with the booking process. Could you share some details about your preferred dates and group size?");
      suggestContactForm();
    } else if (lowerMessage.includes('night') || lowerMessage.includes('club') || lowerMessage.includes('party')) {
      addBotMessage("Our nightlife packages are perfect for experiencing Uganda after dark! We offer VIP club access, guided bar crawls, and exclusive experiences in Kampala and Jinja. Packages start from $30 per person. Would you like more details?");
      suggestContactForm();
    } else {
      addBotMessage("I'd be happy to help you plan your perfect Uganda adventure! What type of experience interests you most?");
      suggestContactForm();
    }
  };

  const suggestContactForm = () => {
    setTimeout(() => setShowForm(true), 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formBody = {
        'form-name': 'chatbot-inquiry',
        ...formData,
        conversation: messages.map(m => `${m.type}: ${m.text}`).join('\n')
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        addBotMessage("Thanks! Our team will contact you soon with personalized recommendations!");
        setShowForm(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          travelDate: '',
          budget: '',
          interests: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      addBotMessage("Sorry, there was an error. Please email us at info@travelcord.guide");
    }
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      addMessage(inputValue, 'user');
      handleBotResponse(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <form name="chatbot-inquiry" data-netlify="true" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="text" name="travelDate" />
        <input type="text" name="budget" />
        <input type="text" name="interests" />
        <input type="text" name="conversation" />
      </form>

      <div className="fixed bottom-4 right-4 z-50">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-16 right-0 w-[320px] sm:w-[380px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-ta-green to-ta-green-dark p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Bot className="h-6 w-6 text-white" />
                    <div>
                      <span className="text-sm text-white font-medium">TravelCord Assistant</span>
                      <p className="text-xs text-white/80">Online | Typically replies in 1 min</p>
                    </div>
                  </div>
                  <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="h-[300px] md:h-[250px] overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] rounded-lg px-4 py-2 ${
                      message.type === 'user'
                        ? 'bg-ta-green text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    }`}>
                      {message.text}
                    </div>
                  </motion.div>
                ))}

                {isTyping && (
                  <div className="flex space-x-1 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg w-20">
                    <div className="w-2 h-2 bg-ta-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-ta-green rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
                    <div className="w-2 h-2 bg-ta-green rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
                  </div>
                )}

                {showForm && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm"
                  >
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="pl-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                            placeholder="Your name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            className="pl-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Travel Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.travelDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, travelDate: e.target.value }))}
                            className="pl-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                            placeholder="When are you planning to travel?"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Budget Range</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.budget}
                            onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                            className="pl-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                            placeholder="Your budget range"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Interests</label>
                        <textarea
                          value={formData.interests}
                          onChange={(e) => setFormData(prev => ({ ...prev, interests: e.target.value }))}
                          rows={2}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                          placeholder="What interests you most about Uganda?"
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors text-sm flex items-center justify-center"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Get Personalized Recommendations
                      </button>
                    </form>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-1 mb-2 overflow-x-auto scrollbar-hide">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={reply.action}
                      className="flex-shrink-0 px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {reply.text}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 text-sm rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-ta-green focus:border-ta-green"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    className="p-2 bg-ta-green text-white rounded-full hover:bg-ta-green-dark transition-colors disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-ta-green text-white p-3 rounded-full shadow-lg hover:bg-ta-green-dark transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen ? <ChevronDown className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
          </motion.div>
        </motion.button>
      </div>
    </>
  );
};

export default ChatBot;